export type AuthProvider = 'auth0' | 'cognito'

export type AuthFlow = 'internal' | 'external'

export function hasAuth0Session(): boolean {
  return Object.keys(localStorage).some((key) => key.includes('@@auth0spajs@@'))
}

export function hasCognitoSession(): boolean {
  const prefix = `oidc.user:${process.env.COGNITO_ISSUER}:`

  return Object.keys(localStorage).some((key) => key.startsWith(prefix))
}

function clearMatchingStorage(matcher: (key: string) => boolean) {
  for (const key of Object.keys(localStorage)) {
    if (matcher(key)) {
      localStorage.removeItem(key)
    }
  }

  for (const key of Object.keys(sessionStorage)) {
    if (matcher(key)) {
      sessionStorage.removeItem(key)
    }
  }
}

function clearAuth0Storage() {
  clearMatchingStorage((key) => key.includes('@@auth0spajs@@'))
}

function clearCognitoStorage() {
  const prefix = `oidc.user:${process.env.COGNITO_ISSUER}:`

  clearMatchingStorage((key) => key.startsWith(prefix))
}

export function detectAuthProvider(): AuthProvider | null {
  const params = new URLSearchParams(window.location.search)
  const provider = params.get('auth_provider')?.toLowerCase()

  if (provider === 'auth0') {
    clearCognitoStorage()
    return 'auth0'
  }

  if (provider === 'cognito') {
    clearAuth0Storage()
    return 'cognito'
  }

  // Existing session continuity
  if (hasCognitoSession()) {
    return 'cognito'
  }

  return null
}

function hasCognitoSessionForClient(clientId: string): boolean {
  const key = `oidc.user:${process.env.COGNITO_ISSUER}:${clientId}`

  return localStorage.getItem(key) !== null
}

export function detectFlow(): AuthFlow {
  const params = new URLSearchParams(window.location.search)

  const flow = params.get('flow')?.toLowerCase()

  if (flow === 'internal' || flow === 'external') {
    return flow
  }

  // Existing Cognito session
  if (hasCognitoSessionForClient(process.env.COGNITO_CLIENT_ID_INTERNAL)) {
    return 'internal'
  }

  if (hasCognitoSessionForClient(process.env.COGNITO_CLIENT_ID_EXTERNAL)) {
    return 'external'
  }

  return 'external'
}

export function detectClientId(): string {
  const provider = detectAuthProvider()

  if (provider === 'auth0') {
    return process.env.AUTH0_CLIENT_ID_CSP_PORTAL
  }

  const flow = detectFlow()

  if (flow === 'internal') {
    return process.env.COGNITO_CLIENT_ID_INTERNAL
  }

  return process.env.COGNITO_CLIENT_ID_EXTERNAL
}

export function redirectToDiscovery() {
  const currentUrl = new URL(window.location.href)

  const discoveryUrl = new URL('/login/discover', process.env.CSP_ID_BASE_URL)
  discoveryUrl.searchParams.set('post_discovery_url', currentUrl.toString())
  window.location.replace(discoveryUrl.toString())
}

export function cleanAuthParamsFromUrl() {
  const url = new URL(window.location.href)

  const authParams = [
    'auth_provider',
    'flow',
    'hint',
    'code',
    'state',
    'session_state',
    'iss',
  ]

  authParams.forEach((param) => url.searchParams.delete(param))

  window.history.replaceState({}, document.title, url.toString())
}

export async function clearAuthState() {
  const authnStateUrl = new URL('/auth/state', process.env.CSP_ID_BASE_URL)
  return fetch(authnStateUrl, {
    method: "DELETE",
    credentials: "include",
  });
}
